import React, {useEffect, useMemo, useState} from 'react';
import PropTypes from 'prop-types';
import {useTranslation} from 'react-i18next';
import {Space, Tooltip} from 'antd';
import {
  StyledInvoiceSelector,
  StyledInvoiceSelectorBr,
  StyledInvoiceSelectorEmpty,
  StyledInvoiceSelectorEmptyText,
  StyledInvoiceSelectorFormContainer,
  StyledInvoiceSelectorFormContainerButtons,
  StyledInvoiceSelectorFormSpace,
  StyledInvoiceSelectorIconButton,
} from './StyledInvoiceSelector';
import AddInvoiceButton from '../AddInvoiceButton';
import ImagePreviewTooltip from '../ImagePreviewTooltip';
import InvoiceAttachmentPreview from '../InvoiceAttachmentPreview';
import InvoiceRadioSelector from '../InvoiceRadioSelector';
import InvoiceNoReceiptCheckbox from '../InvoiceNoReceiptCheckbox';
import MissingFieldIndicator from '../../../MissingFieldIndicator';
import TransactionExpenseTypeForm from '../TransactionExpenseTypeForm';
import invoicePlaceholderImg from '../../../../static/images/pages/transactions/invoice-placeholder.png';
import {CloseIcon, Download2Icon} from '../../../../icons';
import {fileHelpers, objectHelpers} from "../../../../utils/helpers";
import {expenseTypesConstants} from "../../../../constants";

const headerBtnProps = {size: 'small', type: 'text'};

const {NO_RECEIPT} = expenseTypesConstants;

const InvoiceSelector = ({
  attachment,
  expense,
  displayMissingFieldIndicator = false,
  isCloseDisable,
  isDownloadDisable = false,
  onAddInvoice,
  onSelectInvoice,
  onRemoveInvoice,
  onSubmitInvoice,
  invoices = [],
  ...rest
}) => {
  const [t] = useTranslation(['main', 'invoices']);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [noReceiptChecked, setNoReceiptChecked] = useState(false);

  const isFileAttached = useMemo(() => !!(attachment && attachment.file), [attachment]);

  const isDefinedExpense = useMemo(() => {
    if (!expense) return false;
    return objectHelpers.isValidValue(expense?.expense_type);
  }, [expense]);

  useEffect(() => {
    const isNoReceipt = expense?.expense_type === NO_RECEIPT;
    if (noReceiptChecked !== isNoReceipt) {
      setNoReceiptChecked(isNoReceipt);
    }
  }, [expense?.expense_type]); // eslint-disable-line react-hooks/exhaustive-deps

  const isDownloadEnabled = useMemo(() => isFileAttached && !isDownloadDisable, [isDownloadDisable, isFileAttached]);

  const handleSelectNoReceipt = (e, callbackFunction) => {
    const checked = e?.target?.checked || false;
    setNoReceiptChecked(checked);

    onSubmitInvoice?.({
      data: { expense_type: checked ? NO_RECEIPT : null },
      successCallback: callbackFunction,
      errorCallback: callbackFunction,
    });
  }

  const noReceiptCheckbox = <InvoiceNoReceiptCheckbox onEdit={handleSelectNoReceipt} checked={noReceiptChecked} />;

  const addInvoiceButton = <AddInvoiceButton disabled={isFileAttached} onChange={onAddInvoice} />;

  const imagePreview = ({children, trigger = 'click', ...rest} = {}) => (
    <ImagePreviewTooltip
      classNames={{root: 'image-preview-tooltip-overlay transaction-details'}}
      trigger={trigger}
      title={<InvoiceAttachmentPreview attachment={attachment} {...rest} />}
    >
      {children}
    </ImagePreviewTooltip>
  );

  const headerButton = ({title, ...rest}) => (
    <Tooltip title={title}>
      <StyledInvoiceSelectorIconButton {...headerBtnProps} {...rest} />
    </Tooltip>
  );

  const handleDownload = () => attachment.data && fileHelpers.saveFile(attachment.data);

  const handleRemove = (e) => onRemoveInvoice?.(e);

  const panelHeader = (
    <StyledInvoiceSelectorEmpty>
      <StyledInvoiceSelectorEmptyText>
        <Space size='small' align='center' className='d-flex-child'>
          {t('invoice')}
          {(displayMissingFieldIndicator && !isDefinedExpense) && <MissingFieldIndicator />}
        </Space>
      </StyledInvoiceSelectorEmptyText>
      {addInvoiceButton}
    </StyledInvoiceSelectorEmpty>
  );

  if (isFileAttached) {
    return (
      <StyledInvoiceSelector {...rest}>
        <Space className='w-100' direction='vertical'>
          {panelHeader}
          <StyledInvoiceSelectorFormSpace direction='horizontal' size='middle'>
            <Space size={10}>
              {
                imagePreview({
                  children: (
                    <img
                      alt='invoice placeholer'
                      className='placeholder'
                      src={invoicePlaceholderImg}
                    />
                  ),
                  trigger: isFullscreen ? 'click' : 'hover',
                  handleChangeFullscreen: setIsFullscreen
                })
              }
              {!isCloseDisable && headerButton({
                className: 'remove-invoice-button',
                icon: <CloseIcon />,
                onClick: handleRemove,
                title: t('remove')
              })}
              {isDownloadEnabled && headerButton({
                icon: <Download2Icon />,
                onClick: handleDownload,
                title: t('download')
              })}
            </Space>
            <TransactionExpenseTypeForm expense={expense} onSubmit={onSubmitInvoice} />
          </StyledInvoiceSelectorFormSpace>
        </Space>
      </StyledInvoiceSelector>
    )
  }

  return (
    <StyledInvoiceSelector {...rest}>
      {invoices.length ? (
        <StyledInvoiceSelectorFormContainer>
          <Space size='small' direction='vertical'>
            <span>{t('select')} {t('invoice')}</span>
            <InvoiceRadioSelector
              invoices={invoices}
              onChange={onSelectInvoice}
            />
            <StyledInvoiceSelectorBr>
              {t('or')}
            </StyledInvoiceSelectorBr>
            <StyledInvoiceSelectorFormContainerButtons>
              <Space size='small'>
                {addInvoiceButton}
                {noReceiptCheckbox}
              </Space>
            </StyledInvoiceSelectorFormContainerButtons>
          </Space>
        </StyledInvoiceSelectorFormContainer>
      ) : (
        <Space size='middle' direction='vertical' className='w-100'>
          {panelHeader}
          {noReceiptCheckbox}
        </Space>
      )}
    </StyledInvoiceSelector>
  );
}

InvoiceSelector.propTypes = {
  displayMissingFieldIndicator: PropTypes.bool,
  invoices: PropTypes.array,
  onAddInvoice: PropTypes.func,
  onSelectInvoice: PropTypes.func,
  onRemoveInvoice: PropTypes.func,
  onSubmitInvoice: PropTypes.func,
}

export default InvoiceSelector;
